import { Add, Delete, DragIndicator, Edit } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    IconButton,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import ActionBubbleTooltip from '../../components/ActionBubbleTooltip';
import Loading from '../../components/Loading';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import {
    TUTORIAL_SECTIONS,
    groupTutorialsBySection,
    tutorialPageLabel,
} from '../../helpers/tutorials';

function SortableRow({ item, onEdit, onDelete }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    return (
        <Box
            ref={setNodeRef}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: isDragging ? 'action.hover' : 'background.paper',
                transform: CSS.Transform.toString(transform),
                transition,
                zIndex: isDragging ? 2 : 1,
            }}
        >
            <IconButton
                size="small"
                {...attributes}
                {...listeners}
                aria-label="arrastar para reordenar"
                sx={{ cursor: 'grab' }}
            >
                <DragIndicator fontSize="small" />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={600} noWrap>
                    {item.name}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {(item.page_keys || []).length === 0 ? (
                        <Chip size="small" label="Só em Tutoriais" variant="outlined" />
                    ) : (
                        (item.page_keys || []).map((key) => (
                            <Chip key={key} size="small" label={tutorialPageLabel(key)} variant="outlined" />
                        ))
                    )}
                </Stack>
            </Box>
            <ActionBubbleTooltip title="Editar">
                <IconButton color="primary" onClick={() => onEdit(item.id)} aria-label="editar">
                    <Edit />
                </IconButton>
            </ActionBubbleTooltip>
            <ActionBubbleTooltip title="Remover">
                <IconButton color="error" onClick={() => onDelete(item.id)} aria-label="remover">
                    <Delete />
                </IconButton>
            </ActionBubbleTooltip>
        </Box>
    );
}

function SectionBlock({ section, items, onEdit, onDelete, onReorder }) {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
    const ids = useMemo(() => items.map((item) => item.id), [items]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        if (oldIndex < 0 || newIndex < 0) {
            return;
        }
        onReorder(section.value, arrayMove(items, oldIndex, newIndex));
    };

    return (
        <Paper variant="outlined" sx={{ mb: 2.5, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle1" fontWeight={700}>
                    {section.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Arraste para ordenar · {items.length} vídeo{items.length === 1 ? '' : 's'}
                </Typography>
            </Box>
            {items.length === 0 ? (
                <Box sx={{ px: 2, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Nenhum vídeo nesta seção.
                    </Typography>
                </Box>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                        {items.map((item) => (
                            <SortableRow
                                key={item.id}
                                item={item}
                                onEdit={onEdit}
                                onDelete={onDelete}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            )}
        </Paper>
    );
}

function TutorialVideosAdminPage() {
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checking, setChecking] = useState(!isMasterUser());
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api.get('tutorial-videos')
            .then((response) => {
                const list = response?.data?.data?.items || [];
                setItems(Array.isArray(list) ? list : []);
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setChecking(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!checking && isMaster) {
            load();
        }
    }, [checking, isMaster, load]);

    const grouped = useMemo(() => {
        const map = Object.fromEntries(
            TUTORIAL_SECTIONS.map((section) => [section.value, []]),
        );
        groupTutorialsBySection(items).forEach((section) => {
            map[section.value] = section.items;
        });
        return map;
    }, [items]);

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Remover vídeo?',
            text: 'Ele deixa de aparecer na página de tutoriais e nas telas vinculadas.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, remover',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            api.delete(`tutorial-videos/${id}`)
                .then(() => {
                    Swal.fire('Removido!', 'O vídeo foi removido.', 'success');
                    load();
                })
                .catch(() => {
                    Swal.fire('Erro!', 'Não foi possível remover o vídeo.', 'error');
                });
        });
    };

    const handleReorder = (section, nextItems) => {
        setItems((prev) => {
            const others = prev.filter((item) => item.section !== section);
            return [...others, ...nextItems];
        });
        api.post('tutorial-videos/reorder', {
            section,
            ordered_uuids: nextItems.map((item) => item.id),
        }).catch(() => {
            Swal.fire('Erro!', 'Não foi possível salvar a nova ordem.', 'error');
            load();
        });
    };

    if (checking || (isMaster && loading)) {
        return <Loading />;
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Vídeos tutoriais</Typography>
            </Breadcrumbs>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    mb: 2.5,
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        Vídeos tutoriais
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Gerencie seções, páginas e a ordem de exibição.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/admin/tutorial-videos/new')}
                >
                    Novo vídeo
                </Button>
            </Box>

            {TUTORIAL_SECTIONS.map((section) => (
                <SectionBlock
                    key={section.value}
                    section={section}
                    items={grouped[section.value] || []}
                    onEdit={(id) => navigate(`/admin/tutorial-videos/${id}/edit`)}
                    onDelete={handleDelete}
                    onReorder={handleReorder}
                />
            ))}
        </Box>
    );
}

export default TutorialVideosAdminPage;
